/**
 * Work-sample content block fixtures for F-G10 assignment detail pages.
 *
 * Each block group section is a raw Markdown string matching the shape
 * returned by the BE (BLD2-687). The strings use HTML comment anchors
 * (<!-- block: name -->, <!-- slot: name -->, <!-- subsection: name -->)
 * that the MFE's Markdown parser uses to identify and render sections.
 *
 * Two grade-band variants are provided:
 *   assignmentContentG5  — Primary (Grade 5). Science subject.
 *                          assignment_image_url: Euka-branded placeholder.
 *   assignmentContentG8  — Secondary (Grade 8). History subject.
 *                          assignment_image_url: null (no-image state).
 *
 * These are applied as shared defaults for all assignments in each grade
 * band. Per-assignment overrides can be keyed by UUID in assignmentsDemoData.ts.
 *
 * Note: Senior assignments intentionally have no content blocks — Senior
 * uses a different detail UI that does not consume these fields.
 */

import type {
  AssignmentBlocksApiResponse,
  RubricBlocksApiResponse,
  SupportGuideBlocksApiResponse
} from "@/ts/interfaces/assignments/api/assignmentsApiInterface";
import {
  extractSubsection,
  getSubsectionName,
  stripContentBeforeFirstBlock,
  stripSubsection
} from "@shared/assignments/utils/sectionMarkdown";

const extractAfterSubsection = (
  markdown: string,
  subsectionName: string
): string => {
  const lines = markdown.split("\n");
  const startIndex = lines.findIndex(
    line => getSubsectionName(line) === subsectionName
  );

  if (startIndex === -1) {
    return "";
  }

  return lines.slice(startIndex + 1).join("\n").trim();
};

const createStructuredSupportGuideBlocks = (
  supportGuide: string
): SupportGuideBlocksApiResponse => {
  const body = stripContentBeforeFirstBlock(supportGuide);
  const { subsection: gettingStarted } = extractSubsection(
    body,
    "getting_started"
  );
  const { subsection: helpfulTip } = extractSubsection(body, "helpful_tip");
  const supportGuideSteps = stripSubsection(
    stripSubsection(
      extractAfterSubsection(body, "support_guide_steps"),
      "helpful_tip"
    ),
    "final_check"
  ).trim();

  return {
    getting_started: gettingStarted || undefined,
    helpful_tip: helpfulTip || undefined,
    support_guide_steps: supportGuideSteps || undefined
  };
};

// ── Primary Grade 5 ──────────────────────────────────────────────────────────
// Subject: Science — "Living Things Investigation"

const assignmentBlocksG5: AssignmentBlocksApiResponse = {
  assignment_overview:
    "## Assignment overview\n\n" +
    "<!-- block: assignment_overview -->\n" +
    "Living things come in an enormous variety of shapes, sizes, and habitats. Scientists study them by observing carefully, recording what they see, and looking for patterns.\n\n" +
    "This term you have been learning about the features that living things share — they grow, respond to their surroundings, need energy, and reproduce. You have also explored how plants and animals depend on each other and on their environment.\n\n" +
    "In this assignment you will go outside, observe living things in your local area, and create a poster or digital presentation that shows what you found and explains what living things need to survive.\n\n" +
    "<!-- subsection: curriculum_connection -->\n" +
    "### Curriculum connection\n\n" +
    "<!-- slot: curriculum_codes -->\n\n" +
    "| Code | Description |\n" +
    "|---|---|\n" +
    "| ST3-4LW-S | Examines how the environment affects the growth and survival of living things. |\n" +
    "| ST3-1WS-S | Plans and conducts scientific investigations to answer questions and solve problems. |\n\n" +
    "---",

  instructions:
    "## Instructions\n\n" +
    "<!-- block: instructions -->\n" +
    "Go outside and observe living things in your local area. You might look in your backyard, a park, a garden, or along a footpath. Spend at least twenty minutes looking carefully. You can observe plants, animals, insects, birds, or any other living thing you can find.\n\n" +
    "While you are outside, record what you see. You can draw pictures, take photos, or write notes. Try to notice at least five different living things and record where you found each one, what it looks like, and what it seems to need to survive (food, water, sunlight, shelter, space).\n\n" +
    "Back inside, choose your best observations and create a poster or digital presentation that shows your findings and explains what living things need to survive.\n\n" +
    "<!-- slot: work_should_show -->\n" +
    "### Your work should show\n\n" +
    "- at least five different living things you observed, each named and described\n" +
    "- where you found each living thing and what its habitat looks like\n" +
    "- what each living thing needs to survive (food, water, sunlight, shelter, or space)\n" +
    "- at least one connection between a living thing and its environment (for example, a bee visiting a flower)\n" +
    "- your own drawings, photos, or written descriptions from your observation\n\n" +
    "<!-- slot: work_must_include -->\n" +
    "### Your work must include\n\n" +
    "- **A poster or digital presentation** showing:\n" +
    "  - the living things you observed (with drawings or photos)\n" +
    "  - where each one was found\n" +
    "  - what each one needs to survive\n" +
    "- **A written explanation** (at least three sentences) describing one connection you noticed between a living thing and its environment\n\n" +
    "<!-- block: closing_line -->\n" +
    "Before you finish, read back through your work and check that each living thing has a name, a location, and at least one survival need listed. A friend or family member should be able to look at your poster and understand what you found.\n\n" +
    "---",

  what_to_include:
    "## What to include\n\n" +
    "<!-- slot: what_to_include -->\n" +
    "- A poster or digital presentation with at least five living things\n" +
    "- A written explanation of one connection you noticed\n" +
    "- Your own drawings, photos, or written observations\n" +
    "- Saved as a PDF or image file\n\n" +
    "---",

  what_success_looks_like:
    "## What success looks like\n\n" +
    "<!-- slot: success_criteria -->\n" +
    "- I found and recorded at least five different living things\n" +
    "- I named each living thing and described where I found it\n" +
    "- I identified what each living thing needs to survive\n" +
    "- I described at least one connection between a living thing and its environment\n" +
    "- I included my own drawings, photos, or written observations\n" +
    "- My poster or presentation is clear and easy to understand\n" +
    "- I checked my work before finishing\n\n" +
    "---"
};

const rubricBlocksG5: RubricBlocksApiResponse = {
  marking_rubric:
    "# Marking rubric\n\n" +
    "<!-- block: rubric_intro -->\n" +
    "The rubric below shows what each level of achievement looks like for this assignment. Use it as a checklist while you work. The Support Guide gives you strategies for each step.\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Describing living things and their needs\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "ST3-4LW-S\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Examines how the environment affects the growth and survival of living things.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| Clearly identifies at least five living things, accurately describes their survival needs, and explains a connection between a living thing and its environment using specific observations. | Identifies at least five living things and describes their survival needs with mostly accurate detail. A connection to the environment is described. | Identifies at least three living things. Survival needs are listed but not fully explained. A connection to the environment is attempted. | Fewer than three living things are identified, or survival needs and environmental connections are missing or inaccurate. |\n\n" +
    "<!-- slot: parent_observations -->\n" +
    "_(Space for a parent or teacher to note observations about the student's approach and effort during this assignment.)_"
};

const supportGuideBlocksG5: SupportGuideBlocksApiResponse =
  createStructuredSupportGuideBlocks(
    "# Support guide\n\n" +
    "<!-- subsection: getting_started -->\n" +
    "## Getting started\n\n" +
    "<!-- block: getting_started -->\n" +
    "The Assignment sheet has the full task brief. This guide adds extra support for each step. You will find tips for choosing a good observation spot, a simple recording table you can copy, guidance on identifying living things, and a checklist to use before you finish. Work through the steps in order.\n\n" +
    "<!-- slot: skills_demonstrated -->\n" +
    "### Skills demonstrated\n\n" +
    "- observing and recording living things carefully\n" +
    "- identifying features that living things share\n" +
    "- explaining what living things need to survive\n" +
    "- noticing connections between living things and their environments\n" +
    "- presenting your findings clearly\n\n" +
    "<!-- slot: thinking_prompts -->\n" +
    "### Thinking prompts\n\n" +
    "- What is the most interesting living thing you found, and what makes it suited to where it lives?\n" +
    "- If you had to explain to a younger student what living things need to survive, what would you say?\n" +
    "- Is there anything that surprised you during your observation — something you did not expect to find?\n" +
    "- If you look at your poster, does each living thing clearly show where it lives and what it needs?\n\n" +
    "<!-- subsection: support_guide_steps -->\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 4: Write your environmental connection\n\n" +
    "Choose one living thing from your observation and write at least three sentences explaining the connection between it and its environment. A strong explanation names the living thing, describes where it lives, and explains exactly how the environment helps it survive (or how the living thing helps the environment).\n\n" +
    "**Example:** The spider I found under the garden bench lives in a sheltered spot that protects it from wind and rain. It built its web between the bench legs where small insects fly past. The web is sticky so the spider can catch food without chasing it.\n\n" +
    "Your explanation does not need to be this long — three clear sentences that name the living thing, describe its location, and explain the connection will earn full marks for this section.\n\n" +
    "<!-- slot: final_check -->\n" +
    "### Before you finish\n\n" +
    "- At least five living things are included\n" +
    "- Each living thing has a name and a location\n" +
    "- At least one survival need is listed for each\n" +
    "- One environmental connection is described in writing\n" +
    "- My own drawings, photos, or observations are included\n" +
    "- The poster or presentation is neat and easy to read\n" +
    "- Saved as a PDF or image file"
  );

// ── Secondary Grade 8 ────────────────────────────────────────────────────────
// Subject: History — "WWI Causes and Consequences"

const assignmentBlocksG8: AssignmentBlocksApiResponse = {
  assignment_overview:
    "## Assignment overview\n\n" +
    "<!-- block: assignment_overview -->\n" +
    "The First World War reshaped the modern world. It drew in more than thirty nations, killed an estimated seventeen million people, and fundamentally changed how governments organised their societies, economies, and armies.\n\n" +
    "This term you have studied the causes that made war in 1914 possible — the alliance systems, imperial rivalries, nationalism, and the immediate trigger of the Sarajevo assassination — and the consequences that followed, including the human cost, the role of ANZAC forces, and the post-war settlements that shaped the twentieth century.\n\n" +
    "In this assignment you will analyse one cause of the war in depth and evaluate its relative importance compared with at least one other cause, then assess one significant consequence for Australia or the wider world.\n\n" +
    "<!-- subsection: curriculum_connection -->\n" +
    "### Curriculum connection\n\n" +
    "<!-- slot: curriculum_codes -->\n\n" +
    "| Code | Description |\n" +
    "|---|---|\n" +
    "| AC9HH8K01 | The causes and course of World War I, and its significance. |\n" +
    "| AC9HH8K02 | The consequences of World War I, with a particular emphasis on Australia. |\n" +
    "| AC9HH8S04 | Evaluate the reliability and usefulness of sources for a specific historical inquiry. |\n" +
    "| AC9HH8S05 | Develop and support historical arguments using evidence from a range of sources. |\n\n" +
    "---",

  instructions:
    "## Instructions\n\n" +
    "<!-- block: instructions -->\n" +
    "Choose one cause of World War I to analyse in depth. Your options are: the alliance systems, imperial rivalry between European powers, nationalism and the rise of ethnic tensions in the Balkans, or the assassination of Archduke Franz Ferdinand. You will need to do more than describe the cause — you must analyse how it contributed to the outbreak of war and evaluate how important it was relative to at least one other cause.\n\n" +
    "Research your chosen cause using at least three sources. At least one must be a primary source (a document, letter, speech, or image from the period 1870–1918). Record what each source says and assess its reliability and usefulness for your inquiry — consider the author, date, purpose, and any limitations.\n\n" +
    "Write an analytical response that addresses both parts of the task: the analysis of your chosen cause and the assessment of one significant consequence of the war for Australia or the world. Use historical terminology accurately throughout.\n\n" +
    "<!-- slot: work_should_show -->\n" +
    "### Your work should show\n\n" +
    "- a clear analysis of one cause of WWI, explaining how and why it contributed to the outbreak of war\n" +
    "- an evaluation of the relative importance of your chosen cause compared with at least one other cause\n" +
    "- accurate use of historical terminology (alliance, imperialism, nationalism, militarism, sovereignty)\n" +
    "- evidence from at least three sources, with at least one primary source\n" +
    "- an assessment of the reliability and usefulness of at least two of your sources\n" +
    "- an assessment of one significant consequence of WWI for Australia or the wider world\n" +
    "- a clear argument maintained throughout the response\n\n" +
    "<!-- slot: work_must_include -->\n" +
    "### Your work must include\n\n" +
    "- **Analytical response** (600–800 words) addressing:\n" +
    "  - an introduction that states your argument\n" +
    "  - an analysis of your chosen cause and its contribution to the outbreak of war\n" +
    "  - an evaluation of its importance relative to at least one other cause\n" +
    "  - an assessment of one significant consequence of WWI\n" +
    "  - a conclusion that restates and supports your argument\n" +
    "- **Source evaluation** — for at least two sources, note the author, date, purpose, and one strength and one limitation\n" +
    "- **Bibliography** — all sources listed with author, title, date, and (for websites) the URL and date accessed\n\n" +
    "<!-- block: closing_line -->\n" +
    "Before you submit, read your response and check that your argument is clear in the introduction and conclusion, that every historical claim is supported by evidence, and that your source evaluations address reliability and usefulness specifically — not just what the source says.\n\n" +
    "---",

  what_to_include:
    "## What to include\n\n" +
    "<!-- slot: what_to_include -->\n" +
    "- Analytical response of 600–800 words\n" +
    "- Source evaluation for at least two sources\n" +
    "- Bibliography listing all sources\n" +
    "- Saved as a PDF\n\n" +
    "---",

  what_success_looks_like:
    "## What success looks like\n\n" +
    "<!-- slot: success_criteria -->\n" +
    "- I chose one cause of WWI and clearly explained how it contributed to the outbreak of war\n" +
    "- I evaluated how important my chosen cause was compared with at least one other cause\n" +
    "- I used historical terminology accurately throughout my response\n" +
    "- I used evidence from at least three sources, including at least one primary source\n" +
    "- I assessed the reliability and usefulness of at least two of my sources\n" +
    "- I assessed one significant consequence of WWI for Australia or the wider world\n" +
    "- I maintained a clear argument from introduction to conclusion\n" +
    "- My bibliography lists all sources with full details\n" +
    "- I read my response back and edited it before finishing\n\n" +
    "---"
};

const rubricBlocksG8: RubricBlocksApiResponse = {
  marking_rubric:
    "# Marking rubric\n\n" +
    "<!-- block: rubric_intro -->\n" +
    "The rubric below shows what each level of achievement looks like across the key criteria for this assignment. Use it alongside the Support Guide while you draft and edit. Your teacher will use this rubric when assessing the finished response.\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Historical argument and evidence\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9HH8S05\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Develop and support historical arguments using evidence from a range of sources.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| Develops a sustained, well-supported historical argument. Uses evidence from at least three sources, including a primary source, with accurate historical terminology throughout. Evaluates the relative importance of causes with nuance. | Develops a clear historical argument supported by evidence from at least three sources. Uses historical terminology accurately in most places. Evaluates relative importance of causes. | States a historical argument but support from evidence is inconsistent. Uses some historical terminology. Attempts to evaluate relative importance of causes. | Argument is unclear or unsupported. Limited use of evidence or historical terminology. Little or no evaluation of relative importance of causes. |\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Source evaluation\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9HH8S04\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Evaluate the reliability and usefulness of sources for a specific historical inquiry.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| Evaluates at least two sources with specific reference to author, date, purpose, and both a strength and a limitation for each. Evaluation is linked to the specific inquiry. | Evaluates at least two sources addressing most of author, date, purpose, strength, and limitation. Mostly linked to the inquiry. | Evaluates sources but evaluation is incomplete or general rather than linked to the specific inquiry. | Source evaluation is missing or only describes what the source says rather than assessing reliability or usefulness. |\n\n" +
    "<!-- slot: parent_observations -->\n" +
    "_(Open space for a parent or teacher to record observations about the student's research process, drafting, and self-editing.)_"
};

const supportGuideBlocksG8: SupportGuideBlocksApiResponse =
  createStructuredSupportGuideBlocks(
    "# Support guide\n\n" +
    "<!-- subsection: getting_started -->\n" +
    "## Getting started\n\n" +
    "<!-- block: getting_started -->\n" +
    "The Assignment sheet sets out the full task brief. This guide walks you through the research and writing process step by step. You will find a framework for evaluating sources, a guide to historical argument structure, advice on using historical terminology accurately, and a three-pass editing checklist. Work through the steps in order — each one builds on the last.\n\n" +
    "<!-- slot: skills_demonstrated -->\n" +
    "### Skills demonstrated\n\n" +
    "- researching and evaluating primary and secondary sources\n" +
    "- analysing the causes of a significant historical event\n" +
    "- evaluating the relative importance of historical causes\n" +
    "- constructing and sustaining a historical argument\n" +
    "- assessing the consequences of WWI for Australia and the wider world\n" +
    "- using historical terminology accurately in analytical writing\n\n" +
    "<!-- slot: thinking_prompts -->\n" +
    "### Thinking prompts\n\n" +
    "- If your chosen cause had not existed, would war still have broken out in 1914? What does your answer tell you about how important the cause really was?\n" +
    "- What is the strongest piece of evidence you have found, and does it come from someone who was directly involved or someone looking back?\n" +
    "- Is your argument clear enough that someone who has not studied WWI could understand what you are arguing in your first paragraph?\n" +
    "- Have you explained the consequence you chose, or only described it? What is the difference?\n\n" +
    "<!-- subsection: support_guide_steps -->\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 5: Edit in three passes\n\n" +
    "Analytical writing improves significantly with structured editing. Run three passes before you finalise.\n\n" +
    "**Pass one — argument check.** Read only your introduction and conclusion. Does your conclusion restate the same argument as the introduction? Is the argument specific enough that someone could disagree with it? Vague arguments (\"WWI had many causes\") do not score well. Specific ones (\"The alliance systems were the most important structural cause of WWI because they guaranteed that a local conflict would escalate into a continental war\") do.\n\n" +
    "**Pass two — evidence and analysis.** For every historical claim, check that it is supported by named evidence and that you have explained why it supports your argument. Remove any paragraphs that only describe without analysing.\n\n" +
    "**Pass three — terminology and word count.** Read the response aloud. Replace any vague terms (things, stuff, problems, issues) with precise historical language. Check the word count is within 600–800 words. Trim repetition before adding content if you are over the limit.\n\n" +
    "<!-- slot: final_check -->\n" +
    "### Before you finish\n\n" +
    "- The introduction states a clear argument\n" +
    "- One cause of WWI is analysed in depth, not just described\n" +
    "- The relative importance of at least two causes is evaluated\n" +
    "- Evidence from at least three sources is used, including one primary source\n" +
    "- At least two sources are evaluated for reliability and usefulness\n" +
    "- One significant consequence of WWI is assessed\n" +
    "- The conclusion restates and supports the argument\n" +
    "- Historical terminology is used accurately throughout\n" +
    "- The bibliography lists all sources with full details\n" +
    "- The response is 600–800 words\n" +
    "- Saved as a PDF"
  );

// ── Middle Grade 10 ──────────────────────────────────────────────────────────
// Assignment 1: Civics and Citizenship — "Letter to Parliament"
// Content is verbatim from ~/assignments-index-sample (2).json (real BE data).

const assignmentBlocksG10CivicsT1: AssignmentBlocksApiResponse = {
  assignment_overview:
    "## Assignment overview\n\n" +
    "<!-- block: assignment_overview -->\n" +
    "A letter to a Member of Parliament is one of the most direct ways a young Australian can speak about our laws. The Member may have only a few minutes to read the letter. The case has to be tight, with sourced evidence and a clear ask.\n\n" +
    "During this term, you have studied how Australian democracy works and what makes a written argument land in a formal voice.\n\n" +
    "In this assignment, you will write a formal letter to a Member of Parliament about an issue that affects young people in Australia. The letter is a draft for this class. Whether you send it later is a decision you and your parent can make together.\n\n" +
    "<!-- subsection: curriculum_connection -->\n" +
    "### Curriculum connection\n\n" +
    "<!-- slot: curriculum_codes -->\n\n" +
    "| Code | Description |\n" +
    "|---|---|\n" +
    "| AC9HC10K02 | The Australian Government's role and responsibilities at a regional and global level. |\n" +
    "| AC9HC10K05 | The challenges to and ways of sustaining a resilient democracy and a cohesive society in Australia and/or in our region or globally. |\n" +
    "| AC9HC10S04 | Evaluate the methods or strategies related to making decisions about civic participation. |\n" +
    "| AC9HC10S05 | Create descriptions, explanations and arguments using civics and citizenship knowledge, concepts and terms that incorporate evidence. |\n\n" +
    "---",

  instructions:
    "## Instructions\n\n" +
    "<!-- block: instructions -->\n" +
    "Choose an issue that affects young people in Australia. The issue can be national in scope. Examples include online safety, climate policy, youth voting age, mental health services, public transport access, and anti-discrimination law. The issue can also be local with a national-level policy angle, such as a service gap in your area or a regulation that is not working. Pick an issue you can actually research, with public sources you can find. The Support Guide has a list of categories to spark ideas.\n\n" +
    "Research the issue before you start writing. The letter rests on evidence. Find at least three credible sources, note what each one says, and note the source and the date you accessed it. Reputable categories include government reports, peer-reviewed studies, reports from established advocacy or research organisations, and reputable news reporting. The Support Guide walks through how to evaluate whether a source is reputable.\n\n" +
    "Write the letter in formal register addressed to a Member of Parliament. The structure has five working paragraphs plus a sign-off. Open with a clear paragraph that names the issue and the action you are asking for. Explain the Australian Government's role and responsibility on the issue. Explain why the issue matters at a national, regional or global level. Present your evidence with sources named. Make your specific recommendation and briefly explain why writing to an elected representative is a suitable civic participation strategy for this issue. Close with a polite formal sign-off. The Support Guide has a worked structure and sentence stems for each main paragraph.\n\n" +
    "<!-- slot: work_should_show -->\n" +
    "### Your work should show\n\n" +
    "- a clearly identified issue affecting young people in Australia, framed at a national or global level\n" +
    "- understanding of the Australian Government's role and responsibilities on this issue\n" +
    "- analysis of how the issue affects the resilience of Australian democracy or the cohesion of society\n" +
    "- evidence-based reasoning supported by at least three sourced and dated references\n" +
    "- a specific, actionable recommendation the government could realistically take\n" +
    "- a brief explanation of why contacting an elected representative is an appropriate civic participation strategy\n" +
    "- formal register appropriate to addressing a Member of Parliament\n\n" +
    "<!-- slot: work_must_include -->\n" +
    "### Your work must include\n\n" +
    "- **Persuasive letter to a Member of Parliament** (around 400-600 words)\n" +
    "  - a formal opening naming the issue and the action requested\n" +
    "  - one paragraph explaining the Australian Government's role and responsibility on this issue\n" +
    "  - one paragraph analysing how the issue affects democratic resilience or social cohesion\n" +
    "  - one paragraph presenting at least three pieces of supporting evidence with each source named and dated\n" +
    "  - one paragraph making a specific recommendation the government could realistically take, including one sentence on why writing to an elected representative is an appropriate civic participation strategy\n" +
    "  - formal sign-off\n" +
    "- **Sources list** (at the end of the letter or as a footer)\n" +
    "  - each of the at least three sources named with publication or organisation, title or topic, and the date you accessed it\n\n" +
    "<!-- block: closing_line -->\n" +
    "Before you finalise, read the letter back as if you were the Member receiving it. Check that the issue is clear in the first paragraph, that the evidence has sources and dates, and that the recommendation is something the government could actually do. Check that the register stays formal throughout. The Support Guide has worked sentence stems for each paragraph, a framework for evaluating whether a source is reputable, and an editing pass for register.\n\n" +
    "---",

  what_to_include:
    "## What to include\n\n" +
    "<!-- slot: what_to_include -->\n" +
    "- One persuasive letter of around 400-600 words\n" +
    "- One sources list naming each external source and the date you accessed it\n" +
    "- The work checked and edited\n" +
    "- Saved as a PDF\n\n" +
    "---",

  what_success_looks_like:
    "## What success looks like\n\n" +
    "<!-- slot: success_criteria -->\n" +
    "- I chose an issue that affects young people in Australia\n" +
    "- I named what action I want the government to take\n" +
    "- I explained the government's role on this issue\n" +
    "- I analysed how the issue affects democracy or social cohesion\n" +
    "- I used at least three sources, each named and dated\n" +
    "- I made a specific recommendation the government could act on\n" +
    "- I explained why writing to an elected representative is an appropriate civic participation strategy\n" +
    "- I wrote in a formal register suited to a Member of Parliament\n" +
    "- I read the letter back and edited it before finalising\n\n" +
    "---"
};

const rubricBlocksG10CivicsT1: RubricBlocksApiResponse = {
  marking_rubric:
    "# Marking rubric\n\n" +
    "<!-- block: rubric_intro -->\n\n" +
    "The rubric below shows what each level of achievement looks like for the criteria in this assignment. Use it as a guide while you draft and edit. The Support Guide gives you the strategies. This is how the finished piece will be assessed.\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Government Role and Responsibility\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9HC10K02\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "The Australian Government's role and responsibilities at a regional and global level.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| The letter names the specific Australian Government role on this issue, explains any relevant local, national, regional or global responsibility, and ties the role to the recommended action. | The letter names the Australian Government role, explains most relevant responsibility, and links it to the recommendation. | The letter mentions a government role but the relevant responsibility or link to the recommendation is unclear. | The letter does not address the government's role or responsibility on the issue. |\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Democratic Resilience and Social Cohesion\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9HC10K05\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "The challenges to and ways of sustaining a resilient democracy and a cohesive society in Australia and/or in our region or globally.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| The letter analyses how the issue affects democratic resilience or social cohesion, names the specific challenge, and proposes how the recommended action sustains either. | The letter analyses the issue's effect on democracy or cohesion with most of the challenge named and linked to the recommendation. | The letter mentions democracy or cohesion but does not analyse the specific challenge, or the link is unclear. | The letter does not address democratic resilience or social cohesion. |\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Civic Participation Strategy\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9HC10S04\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Evaluate the methods or strategies related to making decisions about civic participation.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| The letter makes a specific, realistic recommendation, explains why writing to an elected representative is an appropriate civic participation strategy, and names what makes the strategy effective for this issue. | The letter makes a realistic recommendation and mostly explains why writing to an elected representative is an appropriate civic participation strategy. | The letter makes a recommendation but the civic participation link is unclear, or the recommendation is not realistic. | The letter makes no clear recommendation and does not address civic participation. |\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Evidence-Based Argument and Acknowledgement\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9HC10S05\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Create descriptions, explanations and arguments using civics and citizenship knowledge, concepts and terms that incorporate evidence.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| The letter builds an argument using accurate civics terms, incorporates at least three pieces of evidence, and names each source with the date accessed. | The letter uses civics terms with most of the three pieces of evidence incorporated and sources mostly named. | The letter uses some civics terms but evidence is not consistently incorporated, or sources are not consistently named. | The letter does not use civics terms accurately, or no evidence sources are named. |\n\n" +
    "<!-- subsection: parent_observations -->\n" +
    "## Parent / Marker observations\n\n" +
    "<!-- slot: parent_observations -->\n" +
    "_(Open space for the parent or marker to record observations about the student's work and process.)_\n\n" +
    "---"
};

const supportGuideBlocksG10CivicsT1: SupportGuideBlocksApiResponse =
  createStructuredSupportGuideBlocks(
    "# Support guide\n\n" +
    "<!-- subsection: getting_started -->\n" +
    "## Getting started\n\n" +
    "<!-- block: getting_started -->\n" +
    "The Assignment carries the task brief and what the letter must include. This guide adds the extras. You will find a list of issue categories to spark a topic, a framework for evaluating whether a source is reputable, a worked letter structure with sentence stems, formal-register guidance, and a three-pass editing process. Work through the steps in order. Each one adds a tool you can apply to the letter.\n\n" +
    "<!-- slot: skills_demonstrated -->\n" +
    "### Skills demonstrated\n\n" +
    "- identifying an issue affecting young people with a national, regional or global policy angle\n" +
    "- researching and evaluating evidence from reputable sources\n" +
    "- explaining the Australian Government's role and responsibilities on a specific issue\n" +
    "- building an evidence-based argument in formal register\n" +
    "- writing a specific, realistic recommendation a Member could act on and explaining why contacting an elected representative is an appropriate civic participation strategy\n\n" +
    "<!-- slot: thinking_prompts -->\n" +
    "### Thinking prompts\n\n" +
    "- what is the strongest single piece of evidence you could use, and where does it come from?\n" +
    "- what specifically would you want the government to do, and is it something the government can actually do?\n" +
    "- if the Member's staff read only the first paragraph, would they know what the letter is asking for?\n" +
    "- which paragraph is doing the most work in your letter, and which could be tightened?\n\n" +
    "<!-- subsection: support_guide_steps -->\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 1: Choose your issue\n\n" +
    "A good letter starts with a clear issue. The issue has to be one you can research. It also has to be one you care about.\n\n" +
    "### Categories that work well for this assignment\n\n" +
    "- **Online safety** (social media age limits, content rules, data privacy for under-18s)\n" +
    "- **Climate action** (emissions targets, clean energy policy, climate-ready infrastructure)\n" +
    "- **Youth voting age** (lowering the voting age to 16, civics in schools, youth voice in policy)\n" +
    "- **Mental health services** (Medicare funding for youth, school counsellor numbers, wait times for care)\n" +
    "- **Public transport** (links to regional areas, low-cost fares for young people, links to school and work)\n" +
    "- **Anti-discrimination law** (gaps in cover, how the law is enforced, religious cases)\n" +
    "- **Education funding** (the cost of senior school, access to TAFE and uni, vocational paths)\n" +
    "- **Migration and refugee policy** (intake numbers, time to process, settlement support)\n\n" +
    "The categories above are starting points, not the only choices. You can pick an issue you care about that is not on the list. Check with your parent that it has a national-level policy angle. Check that you can find at least three reputable sources on it. Pick one issue, then narrow it down. *\"Online safety\"* is too broad. *\"The minimum age for social media accounts\"* is specific enough to research and write about.\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 2: Identify the government's role\n\n" +
    "Once you have your issue, work out which level of government is responsible. The Australian Government is the federal level, based in Canberra. It has clear powers on some issues. It shares powers on others. State and territory governments handle some issues directly.\n\n" +
    "### Quick reference for federal responsibilities\n\n" +
    "- **Clearly federal:** defence, foreign affairs, immigration, taxation, currency, social security, telecommunications, regulation of the digital economy\n" +
    "- **Shared with states:** health, education, environment, housing, transport\n" +
    "- **Mostly state:** schools, hospitals, police, public transport networks (with federal funding flowing in)\n\n" +
    "If your issue sits at the state level, you can still write a federal letter. The recommendation has to be something a federal Member can do. That includes funding, regulation, leadership through National Cabinet or other intergovernmental forums, or a treaty obligation.\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 3: Research and evaluate your evidence\n\n" +
    "The letter rests on at least three pieces of evidence. Each one comes from a reputable source. A reputable source is one the Member's staff can check and trust.\n\n" +
    "### The four-question framework for evaluating a source\n\n" +
    "| Question | What you are checking |\n" +
    "|---|---|\n" +
    "| **Who wrote it?** | Author or group with named expertise (an academic, a government body, a known advocacy group, a peer-reviewed journal) |\n" +
    "| **What evidence is used?** | Data, named studies, official stats. Not opinion shown as fact. |\n" +
    "| **When was it published?** | Recent enough that the data still applies (under 5 years for policy, under 2 years for fast-moving areas) |\n" +
    "| **What is the agenda?** | Every source has a view. Note it. A source with a clear view can still be sound if the evidence it cites can be checked. |\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 4: Draft the letter, structure and stems\n\n" +
    "A persuasive letter to Parliament has a working structure. The structure is not creative writing. It is functional, and it is the same structure the Member's staff see every day.\n\n" +
    "### Worked structure (5 paragraphs plus sign-off)\n\n" +
    "**Opening, the ask, named.** Address the Member, name the issue, name what you are asking for.\n\n" +
    "**Government role.** Name the federal government's role on this issue.\n\n" +
    "**Why it matters.** Explain how the issue affects democratic resilience or social cohesion.\n\n" +
    "**Evidence.** Present your three pieces of evidence. Name each source and the date.\n\n" +
    "**Recommendation and civic participation.** Make your recommendation specific and realistic.\n\n" +
    "**Sign-off.** Polite, formal. *\"Yours sincerely\"* is the standard for a named recipient.\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 5: Register and tone\n\n" +
    "A letter to Parliament is read by adults who deal with hundreds of letters. The register has to match what they expect.\n\n" +
    "### Formal-register checklist\n\n" +
    "- **Address.** *\"Dear [Member's title and surname]\"*\n" +
    "- **Pronouns.** *\"I\"* and *\"my\"* are fine when stating your view.\n" +
    "- **Tone.** Direct, not aggressive. Confident, not demanding.\n" +
    "- **Avoid.** Slang, abbreviations, exclamation marks, sweeping generalisations, unsupported claims.\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 6: Edit in three passes\n\n" +
    "The letter hides different errors in different layers. Run a three-pass check before you call it done.\n\n" +
    "**Pass one, structure.** Read the letter and check each paragraph does one job.\n\n" +
    "**Pass two, evidence and sources.** Find every evidence claim and check it has a named source and a date.\n\n" +
    "**Pass three, register and word choice.** Read the letter aloud. Replace any vague verbs with sharper ones.\n\n" +
    "### A note on sending the letter\n\n" +
    "This letter is a draft you have written for this class. Whether you choose to actually send it to your local Member is a separate decision, and one to make with your parent.\n\n" +
    "<!-- subsection: final_check -->\n" +
    "## Final check before submission\n\n" +
    "<!-- slot: final_check -->\n" +
    "- The letter is around 400 to 600 words\n" +
    "- The opening paragraph names the issue and the action requested\n" +
    "- The letter explains the federal government's role on the issue\n" +
    "- The letter analyses how the issue affects democracy or social cohesion\n" +
    "- At least three pieces of evidence are presented with sources and dates\n" +
    "- The recommendation is specific and realistic\n" +
    "- The letter briefly explains why writing to an elected representative is an appropriate civic participation strategy\n" +
    "- The register is formal throughout\n" +
    "- A sources list is included with each source named and dated\n" +
    "- The work is read back and edited before finalising\n" +
    "- The letter is saved as a PDF"
  );

// ── Middle Grade 10 — Assignment 2: English — "Analysing a Speech" ────────────
// Written to match the same anchor/section structure as the real data above.

const assignmentBlocksG10EnglishT1: AssignmentBlocksApiResponse = {
  assignment_overview:
    "## Assignment overview\n\n" +
    "<!-- block: assignment_overview -->\n" +
    "Speeches are designed to move people — to change what they think, feel, or do. The most effective speeches do this through deliberate choices about language, structure, and delivery.\n\n" +
    "This term you have been studying how authors construct meaning and position audiences. You have analysed how language features such as rhetorical questions, repetition, emotive language, and inclusive pronouns work together to persuade.\n\n" +
    "In this assignment you will select one significant Australian speech and write an analytical response that explains how the speaker uses language and persuasive techniques to achieve their purpose.\n\n" +
    "<!-- subsection: curriculum_connection -->\n" +
    "### Curriculum connection\n\n" +
    "<!-- slot: curriculum_codes -->\n\n" +
    "| Code | Description |\n" +
    "|---|---|\n" +
    "| AC9E10LA01 | Analyse how language features and structures are used to achieve purpose and position audiences in a range of texts. |\n" +
    "| AC9E10LE03 | Develop and justify interpretations of texts using evidence and considered argument. |\n" +
    "| AC9E10LY02 | Select and use evidence from texts to develop and support analytical and persuasive responses. |\n\n" +
    "---",

  instructions:
    "## Instructions\n\n" +
    "<!-- block: instructions -->\n" +
    "Choose one significant Australian speech to analyse. The speech must be publicly available and at least five minutes long when delivered. Suitable examples include Paul Keating's Redfern Address, Gough Whitlam's 1972 election policy speech, Noel Pearson's eulogy for Gough Whitlam, Julia Gillard's misogyny speech, or any speech from the Australian Speeches archive. You may choose a different speech if you can confirm it is publicly available and significant — check with your parent first.\n\n" +
    "Read or listen to the speech at least twice before you start writing. On the second pass, make notes on specific moments where you notice the speaker making a deliberate language choice. Note the technique, the words used, and what effect it has on the audience.\n\n" +
    "Write an analytical response that identifies the speaker's purpose, explains how at least three language or persuasive techniques are used to achieve that purpose, and evaluates how effectively the techniques work together. Use specific evidence — direct quotations or close paraphrase — to support every analytical claim.\n\n" +
    "<!-- slot: work_should_show -->\n" +
    "### Your work should show\n\n" +
    "- a clear identification of the speech's purpose and intended audience\n" +
    "- analysis of at least three specific language or persuasive techniques with named examples from the speech\n" +
    "- explanation of the effect each technique has on the audience\n" +
    "- evaluation of how the techniques work together to achieve the speaker's purpose\n" +
    "- accurate use of metalanguage (rhetorical question, anaphora, emotive language, inclusive pronoun, etc.)\n" +
    "- direct quotations or close paraphrase as evidence for each analytical point\n\n" +
    "<!-- slot: work_must_include -->\n" +
    "### Your work must include\n\n" +
    "- **Analytical response** (500–700 words) structured as:\n" +
    "  - an introduction that identifies the speech, the speaker, the context, and the purpose\n" +
    "  - at least three body paragraphs, each analysing one technique using the PEEL structure (Point, Evidence, Explanation, Link)\n" +
    "  - a conclusion that evaluates how effectively the techniques work together\n" +
    "- **Reference** to the speech at the end of your response (speaker, title, date, and URL or source)\n\n" +
    "<!-- block: closing_line -->\n" +
    "Before you finalise, read each body paragraph and check it contains a named technique, a direct quotation or close paraphrase, and an explanation of effect — not just a description of what the speaker said. A description tells the reader what happened. An analysis explains why the speaker made that choice and what it does to the audience.\n\n" +
    "---",

  what_to_include:
    "## What to include\n\n" +
    "<!-- slot: what_to_include -->\n" +
    "- Analytical response of 500–700 words\n" +
    "- Reference to the speech (speaker, title, date, source)\n" +
    "- The work checked and edited\n" +
    "- Saved as a PDF\n\n" +
    "---",

  what_success_looks_like:
    "## What success looks like\n\n" +
    "<!-- slot: success_criteria -->\n" +
    "- I identified the speech, the speaker, the context, and the purpose in my introduction\n" +
    "- I analysed at least three language or persuasive techniques\n" +
    "- I named each technique using correct metalanguage\n" +
    "- I used a direct quotation or close paraphrase as evidence for each technique\n" +
    "- I explained the effect each technique has on the audience\n" +
    "- I evaluated how the techniques work together in my conclusion\n" +
    "- My response is 500–700 words\n" +
    "- I checked my work before finishing\n\n" +
    "---"
};

const rubricBlocksG10EnglishT1: RubricBlocksApiResponse = {
  marking_rubric:
    "# Marking rubric\n\n" +
    "<!-- block: rubric_intro -->\n\n" +
    "The rubric below shows what each level of achievement looks like for the criteria in this assignment. Use it as a guide while you draft and edit. The Support Guide gives you the strategies. This is how the finished piece will be assessed.\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Analysis of language and persuasive techniques\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9E10LA01\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Analyse how language features and structures are used to achieve purpose and position audiences in a range of texts.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| Identifies and analyses at least three techniques with accurate metalanguage. Each analysis includes a direct quotation or close paraphrase and a clear explanation of the effect on the audience. Evaluates how techniques work together. | Identifies and analyses at least three techniques. Metalanguage is mostly accurate. Evidence is included for most techniques. Effect on audience is explained. | Identifies at least two techniques. Some metalanguage is used. Evidence is present but not consistently linked to analysis. Effect is described rather than explained. | Fewer than two techniques are identified, or metalanguage is absent or inaccurate, or analysis is largely description of content. |\n\n" +
    "<!-- subsection: rubric_criterion -->\n" +
    "## Use of evidence and argument\n\n" +
    "<!-- slot: criterion_code -->\n" +
    "AC9E10LY02\n\n" +
    "<!-- slot: criterion_description -->\n" +
    "Select and use evidence from texts to develop and support analytical and persuasive responses.\n\n" +
    "<!-- slot: criterion_descriptors -->\n" +
    "| Excellent | Sound | Developing | Needs Support |\n" +
    "|---|---|---|---|\n" +
    "| Every analytical claim is supported by direct quotation or close paraphrase. Evidence is integrated fluently and explained. | Most analytical claims are supported by evidence. Evidence is mostly explained. | Some claims are supported by evidence, but evidence is sometimes quoted without explanation. | Little or no textual evidence is used, or evidence is listed without connection to analysis. |\n\n" +
    "<!-- subsection: parent_observations -->\n" +
    "## Parent / Marker observations\n\n" +
    "<!-- slot: parent_observations -->\n" +
    "_(Open space for the parent or marker to record observations about the student's approach and analytical thinking during this assignment.)_\n\n" +
    "---"
};

const supportGuideBlocksG10EnglishT1: SupportGuideBlocksApiResponse =
  createStructuredSupportGuideBlocks(
    "# Support guide\n\n" +
    "<!-- subsection: getting_started -->\n" +
    "## Getting started\n\n" +
    "<!-- block: getting_started -->\n" +
    "The Assignment sheet sets out the full task brief. This guide adds the tools. You will find a reference list of language and persuasive techniques with definitions and examples, a worked PEEL paragraph, guidance on integrating quotations, and a checklist for editing your response. Work through the steps in order.\n\n" +
    "<!-- slot: skills_demonstrated -->\n" +
    "### Skills demonstrated\n\n" +
    "- identifying the purpose and audience of a speech\n" +
    "- recognising and naming language and persuasive techniques accurately\n" +
    "- using textual evidence (direct quotation and close paraphrase) to support analytical claims\n" +
    "- explaining the effect of language choices on an audience\n" +
    "- evaluating how techniques work together to achieve a speaker's purpose\n\n" +
    "<!-- slot: thinking_prompts -->\n" +
    "### Thinking prompts\n\n" +
    "- What does the speaker want the audience to think, feel, or do by the end of the speech?\n" +
    "- Which moment in the speech felt the most powerful to you — and what did the speaker do to create that effect?\n" +
    "- If you removed one of the techniques you identified, would the speech be less effective? Why?\n" +
    "- Is your analysis explaining why the speaker made a choice, or just describing what they said?\n\n" +
    "<!-- subsection: support_guide_steps -->\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 1: Choose and read your speech\n\n" +
    "Pick a speech you find interesting — analysis is easier when you care about the content. Read or listen to it at least twice. On the first pass, follow the argument. On the second pass, notice the language choices. Where does the speaker slow down? Where do they repeat something? Where do they address the audience directly?\n\n" +
    "### Techniques reference\n\n" +
    "| Technique | What it is | Example |\n" +
    "|---|---|---|\n" +
    "| Rhetorical question | A question asked for effect, not to get an answer | \"How long must we wait?\" |\n" +
    "| Anaphora | Repetition of a word or phrase at the start of successive clauses | \"We will fight... we will not stop... we will win.\" |\n" +
    "| Emotive language | Words chosen to trigger an emotional response | \"devastating\", \"heartbreak\", \"hope\" |\n" +
    "| Inclusive pronoun | \"We\" and \"our\" to build a sense of shared identity | \"We are one nation.\" |\n" +
    "| Tricolon | A list of three for emphasis and rhythm | \"Life, liberty, and the pursuit of happiness.\" |\n" +
    "| Direct address | Speaking to the audience as \"you\" to create connection | \"You know this is wrong.\" |\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 2: Write a PEEL paragraph\n\n" +
    "Each body paragraph should follow the PEEL structure.\n\n" +
    "**Point.** State the technique and what it does. *\"Keating uses anaphora to build a sense of shared national responsibility.\"*\n\n" +
    "**Evidence.** Provide a direct quotation or close paraphrase. *\"The repetition of 'we' throughout the address — 'we took the children', 'we committed the murders' — positions every Australian as part of the story.\"*\n\n" +
    "**Explanation.** Explain the effect on the audience. *\"By using the collective pronoun rather than 'they', Keating prevents the audience from distancing themselves from Australia's colonial history.\"*\n\n" +
    "**Link.** Connect back to the speaker's purpose. *\"This technique is central to Keating's purpose: he needs the audience to feel implicated before he can ask them to act.\"*\n\n" +
    "<!-- subsection: support_guide_step -->\n" +
    "<!-- block: step_explanation -->\n" +
    "## Step 3: Integrate quotations smoothly\n\n" +
    "A quotation dropped into a paragraph without introduction or explanation adds little. Integrate evidence using one of these patterns.\n\n" +
    "- **Introduce, quote, explain:** *\"Keating opens with a direct acknowledgement: 'I want to apologise.' The directness of the verb 'apologise' signals that this speech will not hedge.\"*\n" +
    "- **Embed in your sentence:** *\"When Keating says the government 'took the children', the active verb positions the government as agent, not bystander.\"*\n" +
    "- **Close paraphrase with attribution:** *\"Keating's repeated insistence that the stolen generations are not someone else's problem but 'a problem for all of us' uses inclusive language to prevent the audience from opting out.\"*\n\n" +
    "<!-- subsection: final_check -->\n" +
    "## Final check before submission\n\n" +
    "<!-- slot: final_check -->\n" +
    "- The introduction names the speech, the speaker, the context, and the purpose\n" +
    "- At least three techniques are analysed with correct metalanguage\n" +
    "- Each analysis paragraph uses a direct quotation or close paraphrase\n" +
    "- Each analysis explains the effect on the audience — not just what the speaker said\n" +
    "- The conclusion evaluates how the techniques work together\n" +
    "- The response is 500–700 words\n" +
    "- A reference to the speech is included at the end\n" +
    "- The work is read back and edited before finishing\n" +
    "- Saved as a PDF"
  );

const assignmentContentG2Real = {
  assignment_blocks: {
    assignment_overview: `## Assignment overview

<!-- block: assignment_overview -->
Imagine a sunny picnic blanket on the grass. Six teddies are coming. Some bring sandwiches, some bring fruit, some bring drinks. There is a lot to plan.

Now it's your turn. In this assignment, you will plan a teddy bear picnic for 6 toys or family members. You will count the food, sort and draw the picnic shapes, and measure one of the picnic items.


<!-- subsection: curriculum_connection -->
### Curriculum connection

<!-- slot: curriculum_codes -->

| Code | Description |
|---|---|
| AC9M2N01 | Recognise, represent and order numbers to at least 1000 using physical and virtual materials, numerals and number lines. |
| AC9M2N02 | Partition, rearrange, regroup and rename two- and three-digit numbers using standard and non-standard groupings; recognise the role of a zero digit in place value notation. |
| AC9M2SP01 | Recognise, compare and classify shapes, referencing the number of sides and using spatial terms such as "opposite", "parallel", "curved" and "straight". |
| AC9M2M01 | Measure and compare objects based on length, capacity and mass using appropriate uniform informal units and smaller units for accuracy when necessary. |

---`,
    instructions: `## Instructions

<!-- block: instructions -->
Today, you are the picnic planner. Pick 6 teddies or family members for your picnic. Then plan what they will eat.

Count and organise your picnic items. You can use sandwiches, fruit, drinks, or anything else you like. Draw the items on paper. Show how many of each. Sort the items by their shape. Pick one item to measure. Use blocks, the same hand, or pencils to measure it. Place the units end to end with no gaps or overlaps. Write down the number and unit.

Mathematics helps you plan real things. Real picnics need real numbers. The same counting, sorting and measuring you do here helps when a family plans a meal or a birthday party.

<!-- slot: work_should_show -->
### Your work should show
- counting picnic items and writing the number for each one
- sorting picnic items by their shape
- drawing each shape clearly so we can tell them apart
- measuring one item with blocks, hands or pencils
- ideas you came up with yourself

<!-- slot: work_must_include -->
### Your work must include

- **Picnic plan drawing**
  - your 6 teddies or family members
  - the picnic items, drawn or grouped so we can count them
  - the number for each kind of item written next to it
  - at least one picnic number of 100 or more, shown in groups *(for example, 10 groups of 10 grapes)*
- **Shape sorting**
  - picnic items sorted into shape groups *(e.g. circles, rectangles, triangles)*
  - each shape drawn and named
  - one spatial word about each shape *(e.g. curved, straight, opposite, parallel)*
- **Measurement**
  - one picnic item picked to measure
  - the measuring unit named *(e.g. blocks, same hand, pencils)*
  - the same unit placed end to end with no gaps or overlaps
  - the measurement written down with the number and unit

<!-- block: reflection_questions -->
### Reflection questions you must answer

1. How did you count and organise your picnic items?
2. Which shapes did you use most often in your drawings? Describe each shape type.
3. How did you measure one of your picnic items?
4. How did mathematics help plan your picnic?

<!-- block: closing_line -->
When you finish, read your picnic plan with a grown-up. Check that you counted every item, named each shape, wrote your measurement, and answered the reflection questions. The Support Guide has counting tips, shape ideas, sentence starters and a final check.

---`,
    what_to_include: `## What to include

<!-- slot: what_to_include -->
- Your picnic plan drawing, shape sorting, measurement and reflection answers
- Your name and a title for your picnic
- All parts saved together as a PDF, or as separate files / links

---`,
    what_success_looks_like: `## What success looks like

<!-- slot: success_criteria -->
- I picked 6 teddies or family members for my picnic
- I counted my picnic items and wrote the number for each, including one number of 100 or more
- I sorted my picnic items into shape groups
- I drew each shape and named it
- I measured one item with blocks, the same hand or pencils, and wrote the number and unit
- I answered all 4 reflection questions
- My writing is easy to read
- My ideas are clear and organised

---`
  },
  rubric_blocks: {
    marking_rubric: `# Marking rubric

<!-- block: rubric_intro -->

This rubric shows what each level of achievement looks like for the criteria in this assignment. Use it as a guide while you draft and edit. The Support Guide gives you the strategies. This is how the finished piece will be assessed.

<!-- subsection: rubric_criterion -->
## Counting and Representing Numbers

<!-- slot: criterion_code -->
AC9M2N01

<!-- slot: criterion_description -->
Recognise, represent and order numbers to at least 1000 using physical and virtual materials, numerals and number lines.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The picnic plan counts the picnic items accurately, writes matching numbers clearly, includes at least one number of 100 or more, and the reflection explains how the items were counted and organised. | The picnic plan counts the picnic items and writes matching numbers, including one number of 100 or more, with most counting and organising explained in the reflection. | The picnic plan counts some items but some numbers are missing, wrong or only small numbers are used, or the reflection answer is short. | Counting is missing or many numbers are wrong, and the reflection answer is missing. |

<!-- subsection: rubric_criterion -->
## Place Value and Grouping

<!-- slot: criterion_code -->
AC9M2N02

<!-- slot: criterion_description -->
Partition, rearrange, regroup and rename two- and three-digit numbers using standard and non-standard groupings; recognise the role of a zero digit in place value notation.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The picnic plan shows one number of 100 or more grouped clearly, such as 10 groups of 10, and the total matches the items counted. | The picnic plan shows one number of 100 or more with some grouping, and the total mostly matches the items counted. | The picnic plan shows little grouping, uses only small numbers, or some totals do not match the items counted. | Items are not grouped, or the totals are missing or wrong. |

<!-- subsection: rubric_criterion -->
## Shapes and Spatial Words

<!-- slot: criterion_code -->
AC9M2SP01

<!-- slot: criterion_description -->
Recognise, compare and classify shapes, referencing the number of sides and using spatial terms such as "opposite", "parallel", "curved" and "straight".

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The shape sorting names each shape and uses a spatial word for each (e.g. curved, straight, opposite, parallel). | The shape sorting names each shape and uses a spatial word for most. | The shape sorting names some shapes, with few or no spatial words used. | Shapes are not sorted or named, or spatial words are missing. |

<!-- subsection: rubric_criterion -->
## Measuring with Informal Units

<!-- slot: criterion_code -->
AC9M2M01

<!-- slot: criterion_description -->
Measure and compare objects based on length, capacity and mass using appropriate uniform informal units and smaller units for accuracy when necessary.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The measurement names the unit used (blocks, same hand, pencils), writes the number and unit, and shows or explains that the same unit was placed end to end with no gaps or overlaps. | The measurement names the unit and writes the number, with the same unit mostly used correctly. | The measurement is written but the unit is missing, changes during measuring, or gaps/overlaps make the result unclear. | The measurement is missing, or the unit is not named at all. |

<!-- subsection: parent_observations -->
## Parent / Marker observations

<!-- slot: parent_observations -->
_(Open space for the parent or marker to record observations about the child's work and process.)_

---`
  },
  support_guide_blocks: createStructuredSupportGuideBlocks(`# Support guide

<!-- subsection: getting_started -->
## Getting started

<!-- block: getting_started -->
This guide is for the grown-up helping with the task. Read the Assignment with your child first, then use these counting tips, shape ideas, measuring prompts, sentence starters and final check as needed.

<!-- slot: skills_demonstrated -->
### Skills demonstrated
- counting objects and writing the matching number
- grouping items in tens or hundreds to show one number of 100 or more
- sorting objects by their shape
- naming shapes using spatial words
- measuring with informal units like blocks, hands or pencils

<!-- slot: thinking_prompts -->
### Thinking prompts
- who are your 6 picnic guests going to be?
- what foods will the teddies eat?
- which shapes do you see on the picnic blanket?
- what could you use to measure a sandwich?

<!-- subsection: support_guide_steps -->

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 1: Pick your picnic guests

Help your child pick 6 picnic guests. Teddies are easy because they can be lined up on the floor. Family members can work too. Write the names down so your child can count them later.

### Questions to ask

- which 6 teddies or family members will come to the picnic?
- where will the picnic blanket be?
- what time of day is the picnic?

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 2: Count and organise the picnic items

Now help your child plan the food. Sandwiches, fruit, drinks, or anything else. Encourage them to count out loud as they draw. Step back and let them count their own way first.

### Counting prompts

- how many sandwiches do you need so each guest has one?
- can you group the fruit in fives or tens?
- which kind of item do you have the most of?
- write the number next to each kind of item

Help your child include one picnic number of 100 or more, such as 100 grapes, 120 berries, or 100 blanket squares. They can show it in groups, such as 10 groups of 10, so they do not need to draw every item separately.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 3: Sort and draw the picnic shapes

Now look at the shapes. A sandwich is a rectangle. An apple is a circle. A juice cup is a circle on top. Help your child sort the picnic items into shape groups. Then draw each shape on a separate part of the page and name it.

### Shape and spatial words to use

- **Circle.** Curved sides, no corners.
- **Rectangle.** Four straight sides, opposite sides are parallel.
- **Triangle.** Three straight sides.
- **Square.** Four straight sides, all the same length.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 4: Measure one picnic item

Pick one picnic item to measure. A sandwich works well. So does a banana, a juice cup, or a picnic blanket. Then pick one unit. Blocks, the same hand, pencils, or paperclips all work.

### Measuring prompts

- how many blocks long is the sandwich?
- how many hands wide is the picnic blanket?
- how many pencils tall is the juice cup?

Make sure the same unit is used the whole way through. Place each unit end to end with no gaps or overlaps. Write the number and the unit together (e.g. "5 blocks long").

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 5: Answer the reflection questions

Read each question to your child. They can write the answer themselves or say it out loud while you write it down.

### Question 1: How did you count and organise your picnic items?
> *Sentence starter:* "I counted by…" or "I put the items into groups of…"

### Question 2: Which shapes did you use most often in your drawings? Describe each shape type.
> *Sentence starter:* "I used a lot of [shape name]. A [shape name] has…"

### Question 3: How did you measure one of your picnic items?
> *Sentence starter:* "I measured the [item] with [unit]. It was [number] [units] long."

### Question 4: How did mathematics help plan your picnic?
> *Sentence starter:* "Mathematics helped me…" or "Without counting, I could not have…"

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 6: Share it and check together

Once the picnic plan is finished, your child can share it with a teddy, sibling or grandparent. Then check each part together using the Final check.

<!-- subsection: helpful_tip -->
<!-- slot: helpful_tip -->
> **Helpful tip.** A picnic plan does not need to be neat. The counting, the shape names, and the measurement are what matter. Praise the maths first. Tidy the spelling and presentation together, at the very end.

<!-- subsection: final_check -->
## Final check before submission

<!-- slot: final_check -->
- The picnic plan shows 6 teddies or family members
- The picnic items are drawn or grouped, with the number for each written
- One picnic number of 100 or more is shown in groups
- The shapes are sorted and named, with at least one spatial word for each
- One picnic item is measured with the same unit, no gaps or overlaps, and the number and unit written
- All 4 reflection questions are answered
- The picnic has a title
- Your child's name is on the page
- All parts saved together as a PDF, or as separate files / links`)
};

const assignmentContentG5Real = {
  assignment_blocks: {
    assignment_overview: `## Assignment overview

<!-- block: assignment_overview -->
Every animal alive today is the result of millions of years of small changes. A polar bear has thick fur. Thin fur would not last one Arctic winter. A platypus has webbed feet because river-life needs swimming. The features we see in an animal are the ones that helped it survive.

This term, you have explored how living things suit the places they live. You have also seen how scientists build knowledge by working together over time.

For this assignment, you will choose an animal and ask an investigable question. You will research the animal and build a scientific poster. Your poster will explain how its features and behaviours help it survive.

<!-- subsection: curriculum_connection -->
### Curriculum connection

<!-- slot: curriculum_codes -->

| Code | Description |
|---|---|
| AC9S5U01 | Examine how particular structural features and behaviours of living things enable their survival in specific habitats. |
| AC9S5H01 | Examine why advances in science are often the result of collaboration or build on the work of others. |
| AC9S5I01 | Pose investigable questions to identify patterns and test relationships and make reasoned predictions. |
| AC9S5I02 | Plan and conduct repeatable investigations to answer questions including as appropriate deciding the variables to be changed, measured and controlled in fair tests; describing potential risks; planning for the safe use of equipment and materials; and identifying required permissions to conduct investigations on Country/Place. |

---`,
    instructions: `## Instructions

<!-- block: instructions -->
Choose an animal that interests you. It can be a familiar animal (a kangaroo, a magpie, a koala). It can also be one you have read about in books (a snow leopard, an octopus, a hummingbird). Pick an animal you can research. There need to be reliable books or websites about it.

Ask one investigable question about the animal. Then build a labelled scientific poster. The poster shows how the animal's structural features (body parts) and behaviours (things it does) help it survive. Write a short explanation alongside the poster. The explanation ties the features to survival, names your sources, and shows how you researched safely.

Science is built by people working together over time. The reason you can find facts today is that scientists across many years observed and shared what they learned. Your poster takes part in that same tradition. By naming your sources and treating the knowledge with respect, you are doing science the way scientists do.

<!-- slot: work_should_show -->
### Your work should show
- careful research about the animal from reliable sources
- an investigable question and a reasoned prediction about the animal
- explanation of how structural features and behaviours help the animal survive in its habitat
- understanding that scientific knowledge is built by people working together
- safe and respectful research, including respectful treatment of First Nations knowledge if used
- ideas you came up with yourself

<!-- slot: work_must_include -->
### Your work must include
- **Scientific poster** (A3 or A4, hand-drawn or digital)
  - the animal drawn or photographed clearly in the centre
  - at least three structural features labelled, each with how it helps survival
  - at least two behaviours labelled, each with how it helps survival
  - your investigable question and reasoned prediction shown on the poster
  - the habitat of the animal shown or named
- **Written explanation** (around 150-250 words)
  - how the structural features and behaviours work together for survival
  - at least one named scientist, researcher, or source that built up the knowledge
  - how you researched safely, with each source named and the date checked
  - if you used First Nations knowledge, the source is named and Country is acknowledged

<!-- block: closing_line -->
Before you finalise, check that every poster label explains how the feature or behaviour helps the animal survive, and that every claim in the explanation is linked to the poster and to a source. Use the Support Guide for help choosing the animal, writing the question and prediction, checking sources, planning the poster and editing your work.

---`,
    what_to_include: `## What to include

<!-- slot: what_to_include -->
- The scientific poster (A3 or A4)
- The written explanation (around 150-250 words)
- A list of the sources you used, with each source named (site name or URL, book title, or documentary title) and the date checked
- Your name and a title for the poster
- The work checked and edited
- All parts saved together as a PDF, or as separate files / links

---`,
    what_success_looks_like: `## What success looks like

<!-- slot: success_criteria -->
- I chose an animal and an investigable question I could research
- My poster labels three structural features and two behaviours, with how each helps survival
- My written explanation links the features and behaviours to the animal's habitat
- I named at least one scientist, researcher, or source that built up the knowledge
- I researched safely and noted each source with the date I checked it
- I treated any First Nations knowledge about the animal with care and acknowledged Country
- My work is clear, organised, and edited for spelling and punctuation

---`
  },
  rubric_blocks: {
    marking_rubric: `# Marking rubric

<!-- block: rubric_intro -->

This rubric shows what each level of achievement looks like for the criteria in this assignment. Use it as a guide while you draft and edit. The Support Guide gives you the strategies. This is how the finished piece will be assessed.

<!-- subsection: rubric_criterion -->
## Structural Features and Survival

<!-- slot: criterion_code -->
AC9S5U01

<!-- slot: criterion_description -->
Examine how particular structural features and behaviours of living things enable their survival in specific habitats.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The poster labels at least three structural features and two behaviours, and each label explains how it helps the animal survive in its habitat. The written explanation clearly links these features and behaviours to habitat survival. | The poster labels three structural features and two behaviours, with most labels explaining how they help survival. The written explanation links most features or behaviours to habitat survival. | The poster labels some structural features or behaviours, but several labels or explanation points do not explain how they help survival. | The poster has few labels, or the labels and explanation do not connect features or behaviours to survival. |

<!-- subsection: rubric_criterion -->
## How Scientists Build Knowledge

<!-- slot: criterion_code -->
AC9S5H01

<!-- slot: criterion_description -->
Examine why advances in science are often the result of collaboration or build on the work of others.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The written explanation names at least one scientist, researcher, organisation, or reliable source and explains what knowledge it contributed about the animal and how that connects to the poster. | The explanation names a scientist, researcher, organisation, or reliable source and links it to the poster's content. | The explanation mentions a source, but it does not clearly explain what knowledge it contributed or how it links to the poster. | The explanation does not name or credit a relevant source, scientist, researcher, or organisation. |

<!-- subsection: rubric_criterion -->
## Asking Investigable Questions

<!-- slot: criterion_code -->
AC9S5I01

<!-- slot: criterion_description -->
Pose investigable questions to identify patterns and test relationships and make reasoned predictions.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The poster shows a clear investigable question about the animal and a prediction that gives a sensible reason using prior knowledge or observation. | The poster shows an investigable question and a prediction, but the reason for the prediction is brief or partly clear. | The poster shows a question but no prediction, or the prediction is given without a reason. | The poster does not show a question or prediction. |

<!-- subsection: rubric_criterion -->
## Researching Safely and Respectfully

<!-- slot: criterion_code -->
AC9S5I02

<!-- slot: criterion_description -->
Plan and conduct repeatable investigations to answer questions including as appropriate deciding the variables to be changed, measured and controlled in fair tests; describing potential risks; planning for the safe use of equipment and materials; and identifying required permissions to conduct investigations on Country/Place.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The written explanation or source list names each source with the site name or URL, book title, or documentary title, includes the date checked, and explains one safe or reliable research choice. Any First Nations knowledge used is sourced and Country is acknowledged. | The explanation or source list names the sources and gives most dates checked, and any First Nations knowledge used is treated respectfully. | The explanation mentions sources, but some names or dates checked are missing, or the safe/reliable research choice is not clear. | Sources are not listed clearly, or First Nations knowledge is used without a source or acknowledgement where needed. |

<!-- subsection: parent_observations -->
## Parent / Marker observations

<!-- slot: parent_observations -->
_(Open space for the parent or marker to record observations about the student's work and process.)_

---`
  },
  support_guide_blocks: createStructuredSupportGuideBlocks(`# Support guide

<!-- subsection: getting_started -->
## Getting started

<!-- block: getting_started -->
This guide is for the grown-up helping your child with the task. Read the Assignment with your child first. Use this guide to help your child choose an animal, write an investigable question and prediction, check sources, plan the poster, write the explanation and edit before submission.

<!-- slot: skills_demonstrated -->
### Skills demonstrated
- choosing a research question that can be investigated from books or reliable sources
- making a reasoned prediction based on what you already know
- finding and evaluating reliable sources of scientific information
- naming sources with the date you checked them
- treating First Nations knowledge with care and acknowledging Country where relevant
- explaining how features and behaviours help an animal survive in its habitat

<!-- slot: thinking_prompts -->
### Thinking prompts
- which animal is your child curious about and can research from home?
- what does your child already think the animal needs to survive in its habitat?
- which features of the animal seem most important for its survival?
- where can your child find information that is reliable and age-appropriate?

<!-- subsection: support_guide_steps -->

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 1: Choose the animal and the habitat

Help your child choose an animal they find interesting. The animal must be one they can research from home. It can be familiar or new. What matters is whether good information is available.

### Animal choice checks

- the animal lives in a clear habitat the child can describe (forest, ocean, desert, grassland)
- there are at least two good sources about the animal (a book, a documentary, a museum website)
- the animal has features the child can label on a poster (body parts, things it does)
- the animal is right for G5 research (no upsetting content)

Ask your child to name the habitat clearly. Knowing the habitat shapes the rest of the project.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 2: Ask the investigable question and make a prediction

Before the research starts, help your child write one investigable question and a reasoned prediction. The question and the prediction go on the poster.

### Investigable-question pattern

- "How does [feature] help [animal] survive in [habitat]?"
- "Why does [animal] [behaviour] in [habitat]?"
- "What would happen to [animal] if [feature] were different?"

### Prediction shape

A reasoned prediction is more than a guess. It says what your child expects to find AND why.

- "I think the polar bear's fur helps it survive because the Arctic is very cold, and thick fur traps heat."
- "I think the platypus has webbed feet because it swims in rivers, and webbed feet push more water with each kick."

The "because" clause is what makes the prediction reasoned. Your child should write the prediction before the research starts.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 3: Research safely from reliable sources

Now help your child research the animal. The rubric scores the research itself. It also scores how the child names and treats the sources.

### Reliable sources for G5

- **Books** from the library, especially field guides
- **Documentaries** from trusted broadcasters (ABC, BBC, National Geographic)
- **Museum websites** (Australian Museum, Smithsonian, Natural History Museum)
- **University or research-organisation websites** (CSIRO, university zoology pages)
- **Government conservation pages** (Parks Australia, state wildlife agencies)

### Naming each source

For each source your child uses, help them write down:
- **What kind of source it is** (book, documentary, museum website)
- **The name of the source** (book title and author, documentary title, site name, or URL)
- **The date your child checked the source**

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 4: Build the poster

Now help your child build the scientific poster. A3 paper works well, and A4 also works. The poster can be hand-drawn or built digitally.

### Poster-layout sketch

- **Centre.** A drawing or photo of the animal
- **Around the animal.** At least three structural features labelled with arrows, each label saying how the feature helps survival
- **Below or to the side.** At least two behaviours labelled, each with how it helps survival
- **Top or bottom corner.** The investigable question and the reasoned prediction
- **Edge of the poster.** The habitat (drawn or named)
- **Title.** The animal name and a short title

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 5: Write the explanation

The written explanation is around 150-250 words. It links the features and behaviours to survival. It names where the information came from.

### Explanation shape

- **Opening paragraph.** Name the animal, the habitat, and answer the investigable question in one sentence
- **Middle paragraph.** Explain how at least two features and one behaviour work together for survival
- **Closing paragraph.** Name at least one scientist, researcher, or source that built up the knowledge

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 6: Edit in three passes

**Pass one, poster labels.** Does each label explain how the feature or behaviour helps survival?

**Pass two, sources.** Does each source have a name and a date checked? Is any First Nations knowledge sourced clearly?

**Pass three, prediction.** Does the prediction now match what the research showed?

<!-- subsection: helpful_tip -->
<!-- slot: helpful_tip -->
> **Helpful tip.** A scientific poster does not need to be a work of art. The labels, the survival reasoning, and the sources are what the rubric scores. Praise the survival-thinking first. Tidy the spelling on the explanation together, at the very end.

<!-- subsection: final_check -->
## Final check before submission

<!-- slot: final_check -->
- The poster has the animal drawn or shown clearly in the centre
- At least three structural features are labelled with how each helps survival
- At least two behaviours are labelled with how each helps survival
- The investigable question and the reasoned prediction are on the poster
- The habitat is shown or named
- The written explanation links the features and behaviours to survival in the habitat
- The explanation names at least one scientist, researcher, or source that built up the knowledge
- Every source is listed with the source name or URL and the date your child checked it
- Any First Nations knowledge is sourced and Country is acknowledged
- The student's name and a title are on the page
- All parts saved together as a PDF, or as separate files / links`)
};

const assignmentContentG10Real = {
  assignment_blocks: assignmentBlocksG10CivicsT1,
  rubric_blocks: {
    marking_rubric: assignmentBlocksG10CivicsT1 ? `# Marking rubric

<!-- block: rubric_intro -->

The rubric below shows what each level of achievement looks like for the criteria in this assignment. Use it as a guide while you draft and edit. The Support Guide gives you the strategies. This is how the finished piece will be assessed.

<!-- subsection: rubric_criterion -->
## Government Role and Responsibility

<!-- slot: criterion_code -->
AC9HC10K02

<!-- slot: criterion_description -->
The Australian Government's role and responsibilities at a regional and global level.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The letter names the specific Australian Government role on this issue, explains any relevant local, national, regional or global responsibility, and ties the role to the recommended action. | The letter names the Australian Government role, explains most relevant responsibility, and links it to the recommendation. | The letter mentions a government role but the relevant responsibility or link to the recommendation is unclear. | The letter does not address the government's role or responsibility on the issue. |

<!-- subsection: rubric_criterion -->
## Democratic Resilience and Social Cohesion

<!-- slot: criterion_code -->
AC9HC10K05

<!-- slot: criterion_description -->
The challenges to and ways of sustaining a resilient democracy and a cohesive society in Australia and/or in our region or globally.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The letter analyses how the issue affects democratic resilience or social cohesion, names the specific challenge, and proposes how the recommended action sustains either. | The letter analyses the issue's effect on democracy or cohesion with most of the challenge named and linked to the recommendation. | The letter mentions democracy or cohesion but does not analyse the specific challenge, or the link is unclear. | The letter does not address democratic resilience or social cohesion. |

<!-- subsection: rubric_criterion -->
## Civic Participation Strategy

<!-- slot: criterion_code -->
AC9HC10S04

<!-- slot: criterion_description -->
Evaluate the methods or strategies related to making decisions about civic participation.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The letter makes a specific, realistic recommendation, explains why writing to an elected representative is an appropriate civic participation strategy, and names what makes the strategy effective for this issue. | The letter makes a realistic recommendation and mostly explains why writing to an elected representative is an appropriate civic participation strategy. | The letter makes a recommendation but the civic participation link is unclear, or the recommendation is not realistic. | The letter makes no clear recommendation and does not address civic participation. |

<!-- subsection: rubric_criterion -->
## Evidence-Based Argument and Acknowledgement

<!-- slot: criterion_code -->
AC9HC10S05

<!-- slot: criterion_description -->
Create descriptions, explanations and arguments using civics and citizenship knowledge, concepts and terms that incorporate evidence.

<!-- slot: criterion_descriptors -->
| Excellent | Sound | Developing | Needs Support |
|---|---|---|---|
| The letter builds an argument using accurate civics terms, incorporates at least three pieces of evidence, and names each source with the date accessed. | The letter uses civics terms with most of the three pieces of evidence incorporated and sources mostly named. | The letter uses some civics terms but evidence is not consistently incorporated, or sources are not consistently named. | The letter does not use civics terms accurately, or no evidence sources are named. |

<!-- subsection: parent_observations -->
## Parent / Marker observations

<!-- slot: parent_observations -->
_(Open space for the parent or marker to record observations about the student's work and process.)_

---` : ""
  },
  support_guide_blocks: createStructuredSupportGuideBlocks(`# Support guide

<!-- subsection: getting_started -->
## Getting started

<!-- block: getting_started -->
The Assignment carries the task brief and what the letter must include. This guide adds the extras. You will find a list of issue categories to spark a topic, a framework for evaluating whether a source is reputable, a worked letter structure with sentence stems, formal-register guidance, and a three-pass editing process. Work through the steps in order. Each one adds a tool you can apply to the letter.

<!-- slot: skills_demonstrated -->
### Skills demonstrated

- identifying an issue affecting young people with a national, regional or global policy angle
- researching and evaluating evidence from reputable sources
- explaining the Australian Government's role and responsibilities on a specific issue
- building an evidence-based argument in formal register
- writing a specific, realistic recommendation a Member could act on and explaining why contacting an elected representative is an appropriate civic participation strategy

<!-- slot: thinking_prompts -->
### Thinking prompts

- what is the strongest single piece of evidence you could use, and where does it come from?
- what specifically would you want the government to do, and is it something the government can actually do?
- if the Member's staff read only the first paragraph, would they know what the letter is asking for?
- which paragraph is doing the most work in your letter, and which could be tightened?

<!-- subsection: support_guide_steps -->

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 1: Choose your issue

A good letter starts with a clear issue. The issue has to be one you can research. It also has to be one you care about.

### Categories that work well for this assignment

- **Online safety** (social media age limits, content rules, data privacy for under-18s)
- **Climate action** (emissions targets, clean energy policy, climate-ready infrastructure)
- **Youth voting age** (lowering the voting age to 16, civics in schools, youth voice in policy)
- **Mental health services** (Medicare funding for youth, school counsellor numbers, wait times for care)
- **Public transport** (links to regional areas, low-cost fares for young people, links to school and work)
- **Anti-discrimination law** (gaps in cover, how the law is enforced, religious cases)
- **Education funding** (the cost of senior school, access to TAFE and uni, vocational paths)
- **Migration and refugee policy** (intake numbers, time to process, settlement support)

The categories above are starting points, not the only choices. You can pick an issue you care about that is not on the list. Check with your parent that it has a national-level policy angle. Check that you can find at least three reputable sources on it. Pick one issue, then narrow it down. *"Online safety"* is too broad. *"The minimum age for social media accounts"* is specific enough to research and write about.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 2: Identify the government's role

Once you have your issue, work out which level of government is responsible. The Australian Government is the federal level, based in Canberra. It has clear powers on some issues. It shares powers on others. State and territory governments handle some issues directly.

### Quick reference for federal responsibilities

- **Clearly federal:** defence, foreign affairs, immigration, taxation, currency, social security, telecommunications, regulation of the digital economy
- **Shared with states:** health, education, environment, housing, transport
- **Mostly state:** schools, hospitals, police, public transport networks (with federal funding flowing in)

If your issue sits at the state level, you can still write a federal letter. The recommendation has to be something a federal Member can do. That includes funding, regulation, leadership through National Cabinet or other intergovernmental forums, or a treaty obligation.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 3: Research and evaluate your evidence

The letter rests on at least three pieces of evidence. Each one comes from a reputable source. A reputable source is one the Member's staff can check and trust.

### The four-question framework for evaluating a source

| Question | What you are checking |
|---|---|
| **Who wrote it?** | Author or group with named expertise (an academic, a government body, a known advocacy group, a peer-reviewed journal) |
| **What evidence is used?** | Data, named studies, official stats. Not opinion shown as fact. |
| **When was it published?** | Recent enough that the data still applies (under 5 years for policy, under 2 years for fast-moving areas) |
| **What is the agenda?** | Every source has a view. Note it. A source with a clear view can still be sound if the evidence it cites can be checked. |

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 4: Draft the letter, structure and stems

A persuasive letter to Parliament has a working structure. The structure is not creative writing. It is functional, and it is the same structure the Member's staff see every day.

### Worked structure (5 paragraphs plus sign-off)

**Opening, the ask, named.** Address the Member, name the issue, name what you are asking for. The Member should know the topic and the action by the end of this paragraph.

**Government role.** Name the federal government's role on this issue. Distinguish regional from global where the issue has both.

**Why it matters.** Explain how the issue affects democratic resilience or social cohesion.

**Evidence.** Present your three pieces of evidence. Name each source and the date.

**Recommendation and civic participation.** Make your recommendation specific and realistic, then briefly explain why writing to an elected representative is a suitable civic participation strategy for this issue.

**Sign-off.** Polite, formal. *"Yours sincerely"* is the standard for a named recipient.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 5: Register and tone

A letter to Parliament is read by adults who deal with hundreds of letters. The register has to match what they expect.

### Formal-register checklist

- **Address.** *"Dear [Member's title and surname]"*
- **Pronouns.** *"I"* and *"my"* are fine when stating your view. Avoid *"we"* unless you are clearly representing a group.
- **Tone.** Direct, not aggressive. Confident, not demanding.
- **Avoid.** Slang, abbreviations, exclamation marks, sweeping generalisations, unsupported claims.
- **Use sparingly.** Rhetorical questions, emotive language.

<!-- subsection: support_guide_step -->
<!-- block: step_explanation -->
## Step 6: Edit in three passes

The letter hides different errors in different layers. Run a three-pass check before you call it done.

**Pass one, structure.** Read the letter and check each paragraph does one job. If two paragraphs are doing the same job, merge them. If a paragraph is doing two jobs, split it.

**Pass two, evidence and sources.** Find every evidence claim and check it has a named source and a date. The sources list at the end must match every cited claim in the letter.

**Pass three, register and word choice.** Read the letter aloud. Replace any vague verbs (*"deal with"*, *"do something about"*) with sharper ones (*"regulate"*, *"fund"*, *"legislate"*, *"review"*).

### A note on sending the letter

This letter is a draft you have written for this class. Whether you choose to actually send it to your local Member is a separate decision, and one to make with your parent.

<!-- subsection: final_check -->
## Final check before submission

<!-- slot: final_check -->
- The letter is around 400 to 600 words
- The opening paragraph names the issue and the action requested
- The letter explains the federal government's role on the issue
- The letter analyses how the issue affects democracy or social cohesion
- At least three pieces of evidence are presented with sources and dates
- The recommendation is specific and realistic
- The letter briefly explains why writing to an elected representative is an appropriate civic participation strategy
- The register is formal throughout
- A sources list is included with each source named and dated
- The work is read back and edited before finalising
- The letter is saved as a PDF`)
};

// Kept exported so the earlier hand-written fixtures remain available for
// comparison/reference while the demo uses the newer sample-based content.
export const LEGACY_ASSIGNMENT_CONTENT_FIXTURES = {
  assignmentBlocksG5,
  rubricBlocksG5,
  supportGuideBlocksG5,
  rubricBlocksG10CivicsT1,
  supportGuideBlocksG10CivicsT1
};

// ── Exported content objects keyed by grade band ─────────────────────────────
// Used by assignmentsDemoData.ts to attach blocks to the detail response.

export const assignmentContentG5 = assignmentContentG5Real;

export const assignmentContentG8 = {
  assignment_blocks: assignmentBlocksG8,
  rubric_blocks: rubricBlocksG8,
  support_guide_blocks: supportGuideBlocksG8
};

export const assignmentContentG10 = assignmentContentG10Real;

export const assignmentContentG10English = {
  assignment_blocks: assignmentBlocksG10EnglishT1,
  rubric_blocks: rubricBlocksG10EnglishT1,
  support_guide_blocks: supportGuideBlocksG10EnglishT1
};

export const assignmentContentG2 = assignmentContentG2Real;

// Senior assignments return empty blocks from the real API
export const assignmentContentSenior = {
  assignment_blocks: {},
  rubric_blocks: {},
  support_guide_blocks: {}
};
